import { tap } from 'rxjs/operators'
import { Observable } from 'rxjs'
import { AuthService } from '@shared/services/auth.service'
import { HistoryService } from '@shared/services/history/history.service'

/**
 * LogAction dekorátor, amely naplózza a CRUD műveleteket.
 * @param actionName A naplózott művelet neve.
 * @param getLogDetails Függvény, amely meghatározza a naplózási részleteket az adott művelethez.
 */
export function LogAction(
  actionName: string | ((args: any[], result: any) => string),
  getLogDetails: (args: any[], result: any) => any
) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value

    descriptor.value = function (...args: any[]) {
      const historyService: HistoryService = this.historyService
      const authService: AuthService = this.authService

      const result = originalMethod.apply(this, args)

      const determineActionName = (res: any) => {
        if (typeof actionName === 'function') {
          return actionName(args, res)
        }
        return actionName
      }

      const logAction = (res: any) => {
        const userId = authService.getCurrentUser().id
        const action = determineActionName(res)
        const logDetails = getLogDetails(args, res)

        historyService.logAction(action, userId, logDetails)
      }

      if (result instanceof Promise) {
        return result
          .then((res: any) => {
            logAction(res)
            return res
          })
          .catch((error: any) => {
            historyService.logAction(`${actionName}_error`, authService.getCurrentUser().id, { error })
            throw error
          })
      } else if (result instanceof Observable) {
        return result.pipe(
          tap({
            next: (res: any) => {
              logAction(res)
            },
            error: (error: any) => {
              historyService.logAction(`${actionName}_error`, authService.getCurrentUser().id, { error })
            },
          })
        )
      } else {
        logAction(result)
        return result
      }
    }

    return descriptor
  }
}

/**
 * Log details function for create_donation
 */
export function getCreateLogDetails(args: any[], result: any): any {
  const newData = args[0]
  const previousData = {}

  const changedFields = getChangedFields(previousData, newData)

  return {
    recordId: result.key,
    dataChanges: changedFields,
  }
}

/**
 * Log details function for update_donation
 */
export function getUpdateLogDetails(args: any[], result: any): any {
  const previousData = result.previousData || {}
  const newData = args[0]

  const changedFields = getChangedFields(previousData, newData)

  return {
    recordId: args[1],
    dataChanges: changedFields,
  }
}

/**
 * Log details function for delete_donation
 */
export function getDeleteLogDetails(args: any[], result: any): any {
  const previousData = result?.previousData || {}
  const newData = {}

  const changedFields = getChangedFields(previousData, newData)

  return {
    recordId: args[0],
    dataChanges: changedFields,
  }
}

function getChangedFields(previousData: any, newData: any): any {
  const changedFields: any = {}
  const allKeys = new Set([...Object.keys(previousData), ...Object.keys(newData)])

  allKeys.forEach(key => {
    const prevValue = previousData[key]
    const newValue = newData[key]

    if (prevValue !== newValue) {
      changedFields[key] = {
        from: prevValue !== undefined ? prevValue : 'N/A',
        to: newValue !== undefined ? newValue : 'N/A',
      }
    }
  })

  return changedFields
}
